import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Typography, useMediaQuery, Box, Stack } from "@mui/material";
import { mobileWidth, AutostoreTable } from "@qubit/autoparts";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { ShiftSelectSnackbar } from "~/components/ShiftSelectSnackbar";

import { OrderStatus } from "~/features/orders/OrdersSearch";
import { useShiftSelect } from "~/hooks/useShiftSelect";
import { formatTime } from "~/lib/helpers";
import { FulfillmentCenterDto, OrderSummaryDto } from "~/types/api";

type OrderTableProps = {
  formatOrderStatus?: (order: OrderSummaryDto) => string;
  fulfillmentCenter: FulfillmentCenterDto | null;
  onOrderSelected: (order: OrderSummaryDto) => void;
  orders: OrderSummaryDto[];
  ordersLoading: boolean;
  selectedDate: Date | null;
  selectedOrderIds: Guid[];
  title: string;
};

export function OrderTable(props: OrderTableProps) {
  const {
    formatOrderStatus = undefined,
    fulfillmentCenter,
    onOrderSelected,
    orders,
    ordersLoading,
    selectedDate,
    selectedOrderIds,
    title
  } = props;

  const { t } = useTranslation();
  const isMobile = useMediaQuery(mobileWidth);

  const displayDate = selectedDate
    ? dayjs(selectedDate).format("MMM Do")
    : null;

  const displayDay = selectedDate ? dayjs(selectedDate).format("ddd") : null;
  const defaultOrderStatusFormat = (order: OrderSummaryDto) => order.status;
  const orderStatusFormatter = formatOrderStatus || defaultOrderStatusFormat;

  // shift click
  const { shiftIsPressed, handleRowClick, shiftClickFunction } =
    useShiftSelect<OrderSummaryDto>({
      rows: orders,
      selectedRowIds: selectedOrderIds,
      getRowId: (order) => order.orderId,
      onRowSelected: onOrderSelected
    });

  return (
    <>
      <Box id="order-table-container" sx={{ height: "100%" }}>
        <AutostoreTable<OrderSummaryDto>
          title={`${displayDay} ${displayDate}`}
          widthOfCols={["70%", "15%", "15%"]}
          headerColNames={[
            title,
            isMobile ? t("time") : t("window"),
            t("status")
          ]}
          rowId={(row: OrderSummaryDto) => row.orderId}
          renderColumns={[
            (row: OrderSummaryDto) => {
              const { externalOrderId = "", customerName } = row;

              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  gap={isMobile ? "3vw" : "1vw"}
                >
                  <Box>
                    <Typography variant="caption">{customerName}</Typography>
                    <Typography variant="caption" component="div">
                      {externalOrderId}
                    </Typography>
                  </Box>
                  {row.priority.toLowerCase() === "express" && (
                    <Stack direction="column" alignItems="center" gap={0.5}>
                      <AccessTimeIcon fontSize="medium" />
                      <Typography variant="caption">{row.priority}</Typography>
                    </Stack>
                  )}
                </Stack>
              );
            },
            (row) => {
              return (
                <>
                  <Typography variant="body2">
                    {row.pickingEndTime &&
                      formatTime(
                        row.pickingEndTime,
                        fulfillmentCenter?.timeZone
                      )}
                  </Typography>
                </>
              );
            },
            (row: OrderSummaryDto) => (
              <>
                <Typography variant="body2">
                  {" "}
                  {!formatOrderStatus
                    ? t(
                        `${orderStatusFormatter(row).toLowerCase()}` as OrderStatus
                      )
                    : orderStatusFormatter(row)}
                </Typography>
              </>
            )
          ]}
          rowData={orders}
          selectedRows={selectedOrderIds}
          selectRowCallback={(row: OrderSummaryDto) => {
            handleRowClick(row);
          }}
          loading={ordersLoading}
        />
      </Box>
      <ShiftSelectSnackbar isOpen={shiftIsPressed} mode={shiftClickFunction} />
    </>
  );
}
